import { DataIdFromObjectFn } from '@sketch/graphql-cache'
import { NormalizedCacheObject } from 'apollo-cache-inmemory'
import produce from 'immer'
import { broadcastChanges } from './apolloCache'
import ApolloClient from 'apollo-client'

interface Identity {
  __typename: string
}

type ScopeValidator = (key: string) => boolean

/**
 * Empty paginated is used to empty all paginated queries in the cache.
 * This is useful when we might have multiple cached queries for seemingly the same
 * list, e.g. due to search or filters.
 */
export function emptyPaginated<ParentIdentity extends Identity>(
  // we need to pass ApolloClient instead of DataProxy because
  // broadcastChanges doesn't work as expected with DataProxy
  cache: ApolloClient<unknown>,
  scopeValidator:
    | { parent: ParentIdentity; fieldName: string }
    | ScopeValidator,

  paginationUpdater: undefined | ((pagination: unknown) => void) = undefined
) {
  const dataIdFromObject = ((cache as any).cache || cache).config
    .dataIdFromObject as DataIdFromObjectFn

  let updated = false

  const newCache = produce(
    (cache as any).extract(),
    (normalizedCache: NormalizedCacheObject) => {
      const keys = Object.keys(normalizedCache)

      const getIsInScope = (key: string) => {
        if (typeof scopeValidator === 'function') {
          return scopeValidator(key)
        }
        const { parent, fieldName } = scopeValidator
        const parentCacheId = dataIdFromObject(parent)
        if (!parentCacheId) return false

        return key.includes(`${parentCacheId}.${fieldName}`)
      }

      for (const key of keys) {
        const isInScope = getIsInScope(key)
        if (!isInScope) continue

        const cacheEntry = normalizedCache[key]!

        if (key?.includes('.meta')) {
          Object.assign(cacheEntry, {
            after: null,
            totalCount: 0,
          })
          updated = true
        } else if (cacheEntry?.entries) {
          cacheEntry.entries = []
          updated = true
        }
        if (paginationUpdater) {
          paginationUpdater(cacheEntry)
          updated = true
        }
      }
    }
  )

  if (updated) {
    // eslint-disable-next-line no-extra-semi
    ;(cache as any).restore(newCache)

    broadcastChanges(cache)
  }
}
